import { useEffect, useState, type ReactNode } from 'react';
import { X } from 'lucide-react';
import { customerService } from '@/services/customerService';
import type { PhotoQuoteRecipientType } from '@/services/quoteService';
import type { Agent, Customer } from '@/types/domain';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { cn } from '@/lib/utils';

/** An agent can receive a quote only once they can sign in to the Trade Portal. */
export function agentCanReceive(agent: Agent): boolean {
  return agent.portal_access && Boolean(agent.email);
}

interface QuoteRecipientFieldsProps {
  recipientType: PhotoQuoteRecipientType;
  onRecipientTypeChange: (type: PhotoQuoteRecipientType) => void;
  agents: Agent[];
  agentId: string;
  onAgentChange: (agentId: string) => void;
  customer: Customer | null;
  onCustomerChange: (customer: Customer | null) => void;
  /** The recipient can only change while the quote is a draft. */
  disabled: boolean;
  /** Extra cells for the same grid row (the brand filter). */
  children?: ReactNode;
}

/**
 * Who a Photo Quote goes to: a customer (their own portal account) or a sales agent, who may
 * be told which customer it is for. Renders the Send to switch and a two-column grid of fields.
 */
export function QuoteRecipientFields({
  recipientType,
  onRecipientTypeChange,
  agents,
  agentId,
  onAgentChange,
  customer,
  onCustomerChange,
  disabled,
  children,
}: QuoteRecipientFieldsProps) {
  const [customerSearch, setCustomerSearch] = useState('');
  const [customerResults, setCustomerResults] = useState<Customer[]>([]);
  const [customerOpen, setCustomerOpen] = useState(false);
  const isAgent = recipientType === 'agent';
  const hasAgentsWithoutAccess = agents.some((agent) => !agentCanReceive(agent));

  useEffect(() => {
    if (!customerOpen) return;
    const timer = setTimeout(() => {
      customerService
        .list({ search: customerSearch.trim() || undefined, limit: 20, sort_by: 'company_name' })
        .then((result) => setCustomerResults(result.data))
        .catch(() => setCustomerResults([]));
    }, 250);
    return () => clearTimeout(timer);
  }, [customerSearch, customerOpen]);

  return (
    <>
      <div className="space-y-1.5">
        <Label id="quote-send-to">Send to</Label>
        <div
          role="group"
          aria-labelledby="quote-send-to"
          className="inline-flex rounded-md border p-0.5"
        >
          {(['customer', 'agent'] as const).map((type) => (
            <button
              key={type}
              type="button"
              aria-pressed={recipientType === type}
              disabled={disabled}
              onClick={() => onRecipientTypeChange(type)}
              className={cn(
                'min-h-8 rounded px-4 text-sm font-medium transition-colors disabled:cursor-not-allowed disabled:opacity-60',
                recipientType === type
                  ? 'bg-primary text-primary-fg'
                  : 'text-muted-foreground hover:bg-accent hover:text-foreground',
              )}
            >
              {type === 'customer' ? 'Customer' : 'Agent'}
            </button>
          ))}
        </div>
      </div>

      <div className="grid gap-3 sm:grid-cols-2">
        {isAgent && (
          <div className="space-y-1.5">
            <Label htmlFor="quote-agent">Agent</Label>
            <select
              id="quote-agent"
              value={agentId}
              onChange={(e) => onAgentChange(e.target.value)}
              disabled={disabled}
              className="h-9 w-full rounded-md border border-input bg-transparent px-3 text-sm shadow-xs disabled:opacity-60"
            >
              <option value="">Choose an agent</option>
              {agents.map((agent) => {
                const ok = agentCanReceive(agent);
                return (
                  <option key={agent.id} value={agent.id} disabled={!ok}>
                    {ok ? agent.name : `${agent.name} — No portal access`}
                  </option>
                );
              })}
            </select>
            {hasAgentsWithoutAccess && (
              <p className="text-xs text-muted-foreground">
                No portal access — set it in Agent Management.
              </p>
            )}
          </div>
        )}

        <div className="space-y-1.5">
          <Label htmlFor="quote-customer">{isAgent ? 'For customer (optional)' : 'Customer'}</Label>
          <div className="relative">
            <Input
              id="quote-customer"
              value={customerOpen ? customerSearch : customer?.company_name ?? ''}
              placeholder="Search customers"
              onFocus={() => setCustomerOpen(true)}
              onBlur={() => setCustomerOpen(false)}
              onChange={(e) => setCustomerSearch(e.target.value)}
              disabled={disabled}
              className={cn(isAgent && customer && 'pr-8')}
            />
            {isAgent && customer && !customerOpen && !disabled && (
              <button
                type="button"
                onClick={() => onCustomerChange(null)}
                className="absolute right-2 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground"
                aria-label="Clear customer"
              >
                <X className="size-4" />
              </button>
            )}
            {customerOpen && customerResults.length > 0 && (
              <ul className="absolute z-20 mt-1 max-h-64 w-full overflow-auto rounded-md border bg-popover p-1 shadow-md">
                {customerResults.map((row) => (
                  <li key={row.id}>
                    <button
                      type="button"
                      className="w-full rounded px-2 py-1.5 text-left text-sm hover:bg-accent"
                      onMouseDown={(e) => e.preventDefault()}
                      onClick={() => {
                        onCustomerChange(row);
                        setCustomerOpen(false);
                        setCustomerSearch('');
                      }}
                    >
                      <span className="block truncate">{row.company_name}</span>
                      <span className="block truncate text-xs text-muted-foreground">
                        {row.contact_name || row.email || '—'}
                      </span>
                    </button>
                  </li>
                ))}
              </ul>
            )}
          </div>
        </div>

        {children}
      </div>
    </>
  );
}
